import { useAssetVizInternal } from '@asset-viz/AssetVizInternalProvider'
import { Ruler3D } from '../decor/Ruler3D'
import { LegacyBoilerWallFeature3D } from '../features/legacyBoilerWalls/LegacyBoilerWallFeature3D'
import { planeGeometry } from '@asset-viz/utils/geometryDefines'
import { Vec3 } from '@asset-viz/utils/helpers'
import { useAssetVizLocale } from '@asset-viz/providers/locale'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoLegacyBoilerWall,
  GeckoLegacyBoilerWalls,
  GeckoMeasurement,
  TUBESPACING_DEFAULT,
} from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing any number of conjoined "legacy" boiler walls
 * @param model               The "boiler walls" component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const LegacyBoilerWallsSurface3D: React.FC<
  {
    model: GeckoLegacyBoilerWalls
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { showFeatures } = useAssetVizInternal()

  const { formatRange } = useAssetVizLocale()

  const { bounds, scale, walls, tiltRulers, tiltNameLabels } =
    React.useMemo(() => {
      const walls: Array<GeckoLegacyBoilerWall> = model.wallsArray
      const tubeSpacing = model.tubeSpacing ?? TUBESPACING_DEFAULT

      const maxCharCountSquare = 64 // this is the max characters that fit on 1:1 aspect ratio

      const tiltNameLabels = !walls.every((wall) => {
        const aspectRatio =
          wall.geometry.width.convertFromTubes(tubeSpacing).num /
          wall.geometry.height.num
        const maxCharCount = maxCharCountSquare * aspectRatio
        return wall.name.length < maxCharCount
      })

      const tiltRulers = !walls.every((wall) => {
        const aspectRatio =
          wall.geometry.width.convertFromTubes(tubeSpacing).num /
          wall.geometry.height.num
        const maxCharCount = maxCharCountSquare * aspectRatio
        const wallXCustomerBounds = wall.customerGeometry.x
        const rangeString = formatRange({
          start: wallXCustomerBounds.start.num,
          end: wallXCustomerBounds.end.num,
          inputUnit: wallXCustomerBounds.unit,
          variant: 'geometric',
        })
        return rangeString.length < maxCharCount
      })

      const scale = new Vector3(
        model.componentBounds.width.convertFromTubes(tubeSpacing).num,
        model.componentBounds.height.num,
        1.0
      )

      return {
        bounds: model.componentBounds,
        scale,
        tubeSpacing,
        walls,
        tiltNameLabels,
        tiltRulers,
      }
    }, [model, formatRange])

  const geometry = React.useMemo(() => planeGeometry, [])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      return [relX, 0, 0]
    },
    [bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(bounds.y.unit)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      return [0, relY, 0]
    },
    [bounds]
  )

  const displayXRuler = React.useMemo(
    () =>
      !walls.reduce(
        (currentVal, wall) =>
          currentVal ||
          (typeof showFeatures === 'boolean'
            ? showFeatures
            : showFeatures({ feature: wall, componentModel: model })),
        false
      ),
    [walls, model, showFeatures]
  )

  return (
    <Surface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      // Apply the true scaling last (override anything in groupProps)
      scale={scale}>
      {!displayXRuler ? null : (
        <Ruler3D
          anchor={'top'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionXTick}
        />
      )}
      <Ruler3D
        anchor={'right'}
        start={model.componentBounds.start.y}
        end={model.componentBounds.end.y}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionYTick}
      />
      {walls.map((wall) => (
        <LegacyBoilerWallFeature3D
          key={wall.id}
          model={wall}
          tiltNameLabel={tiltNameLabels}
          tiltRuler={tiltRulers}
        />
      ))}
    </Surface3D>
  )
}
